import { trace, SpanStatusCode } from '@opentelemetry/api';
import { getBreadcrumbs } from './breadcrumbs';
import { LogContext } from './observability.types';

const MAX_ATTR_CHARS = 4000;
const MAX_SPAN_NAME_CHARS = 120;

// Engine state, populated by initLoggerCore() at startup. Kept here (rather than
// in index.ts) so logger and the SDK instance share module state without a
// circular import.
type UptraceLike = {
  reportException?: (err: Error) => void;
  forceFlush?: () => Promise<unknown>;
} | null;

let uptrace: UptraceLike = null;
let getCrashState: (() => Record<string, unknown> | undefined) | undefined;
let userAttrs: LogContext = {};
let tracerName = 'app.logger';

// Errors already reported, used to drop duplicates: the same Error instance can
// reach reportError twice. In dev, React re-throws an error caught by an
// ErrorBoundary to window.onerror, so both componentDidCatch and the global
// handler fire for a single crash.
const reportedErrors = new WeakSet<Error>();

/** Wire the engine with the started SDK instance and the host's crash-state provider. */
export const initLoggerCore = (
  instance: UptraceLike,
  crashStateProvider?: () => Record<string, unknown> | undefined,
  scope?: string,
): void => {
  uptrace = instance;
  getCrashState = crashStateProvider;
  if (scope) tracerName = `${scope}.logger`;
};

const toError = (msg: string | Error): Error =>
  typeof msg === 'string' ? new Error(msg) : msg;

const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max)}…[truncated]`;

// Span name shown in Uptrace: the error's first message line (truncated), so
// errors are distinguishable instead of all appearing as a generic "log.error".
const errorSpanName = (err: Error): string =>
  truncate((err.message?.trim() || err.name || 'error').split('\n')[0], MAX_SPAN_NAME_CHARS);

// Defensive serialization: error/crash reporting must never throw. JSON.stringify
// raises on circular references and BigInt, which on the crash path (window.onerror,
// ErrorBoundary, unhandledrejection) would swallow the very error being reported.
const safeStringify = (v: unknown): string => {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return '[unserializable]';
  }
};

const flatAttrs = (ctx?: LogContext): Record<string, string> => {
  const merged = { ...userAttrs, ...(ctx ?? {}) };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(merged)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === 'string' ? v : safeStringify(v);
  }
  return out;
};

const buildCrashContext = (): Record<string, string> => {
  const out: Record<string, string> = {};

  const breadcrumbs = getBreadcrumbs();
  if (breadcrumbs.length > 0) {
    out['crash.breadcrumbs'] = truncate(safeStringify(breadcrumbs), MAX_ATTR_CHARS);
  }

  const state = getCrashState?.();
  if (state) {
    out['crash.state'] = truncate(safeStringify(state), MAX_ATTR_CHARS);
  }

  return out;
};

const reportError = (err: Error, ctx?: LogContext) => {
  if (!uptrace) return;
  // Drop duplicate reports of the same Error instance (see reportedErrors).
  if (reportedErrors.has(err)) return;
  reportedErrors.add(err);

  const attrs = {
    'log.severity': 'error',
    ...flatAttrs(ctx),
    ...buildCrashContext(),
  };

  // Record the exception on an explicit error span. We call reportException
  // from WITHIN the active span scope on purpose: with an active span the
  // Uptrace helper attaches the exception to that span (span.recordException),
  // whereas outside a span it emits a separate OTLP log to /v1/logs — which
  // would duplicate this error on the logs pipeline. Fall back to recording
  // manually when the helper is absent or throws.
  const tracer = trace.getTracer(tracerName);
  tracer.startActiveSpan(errorSpanName(err), { attributes: attrs }, (span) => {
    try {
      if (uptrace?.reportException) {
        uptrace.reportException(err);
      } else {
        span.recordException(err);
      }
    } catch (e) {
      span.recordException(err);
      console.warn('[logger] uptrace.reportException threw', e);
    }
    span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
    span.end();
  });

  // Force the BatchSpanProcessor to flush so the error reaches the collector
  // immediately rather than waiting for the next batch tick. This is fire-and-
  // forget: on an immediate reload/navigation the last span may not make it out.
  if (typeof uptrace.forceFlush === 'function') {
    uptrace.forceFlush().catch((e) => console.warn('[logger] forceFlush failed', e));
  }
};

const emitLogSpan = (level: 'warn' | 'info', message: string, ctx?: LogContext) => {
  if (!uptrace) return;
  const tracer = trace.getTracer(tracerName);
  const span = tracer.startSpan(`log.${level}`, {
    attributes: {
      'log.severity': level,
      'log.message': message,
      ...flatAttrs(ctx),
    },
  });
  span.end();
};

export const logger = {
  error(msg: string | Error, ctx?: LogContext): void {
    console.error(msg, ctx);
    reportError(toError(msg), ctx);
  },

  warn(message: string, ctx?: LogContext): void {
    console.warn(message, ctx);
    emitLogSpan('warn', message, ctx);
  },

  info(message: string, ctx?: LogContext): void {
    console.info(message, ctx);
    emitLogSpan('info', message, ctx);
  },

  setUser(id: string, email?: string): void {
    userAttrs = {
      'user.id': id,
      ...(email ? { 'user.email': email } : {}),
    };
  },

  clearUser(): void {
    userAttrs = {};
  },
};
